import { inject, Injectable } from '@angular/core';
import { getIterationId, getPageIterationId, processPath } from '../utilities.service';
import { ApplicationApiService } from '../application-api/application-api.service';
import { FeatureFlagService } from '@cudirect/shared-ui-library';
import { PrimitiveType, LogicFnRef } from '@workflow/class/workflow.type';
import { LogicService, LogicFnContext } from '@workflow/service/logic/logic.service';
import { LoanType } from '@shared/enum';
import { StatefulService } from '@app/service/stateful.service';

@Injectable({
  providedIn: 'root'
})
export class OnClickFunctionsService {
  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly logicservice: LogicService,
    public readonly applicationApiService: ApplicationApiService,
    public readonly featureFlagService: FeatureFlagService
  ) {}
  /**
   * Generic value set
   * @param context field context
   * @param pathArg storage path
   * @param value new value
   */
  public setValue = (context: LogicFnContext, pathArg: string, value: PrimitiveType): void => {
    const path = processPath(pathArg, context);
    let iterationId: number;
    if (context.field && context.field.iterationId >= 0) {
      iterationId = context.field?.iterationId;
    } else if (context.card?.iterationId >= 0) {
      iterationId = context.card?.iterationId;
    }
    this.statefulService.wrap(path, iterationId).value = value;
  };

  /**
   * Call this function only when page is valid and ready to go next. Set Generic value and navigates to next page
   * @param context field context
   * @param pathArg storage path
   * @param value new value
   */
  public setValueAndNavigateNext = (context: LogicFnContext, pathArg: string, value: PrimitiveType): void => {
    if (!context.page.helper.isPageComplete) {
      return;
    }

    this.setValue(context, pathArg, value);
    if (context.defaultNavigationFn) {
      context.defaultNavigationFn();
    }
  };

  /**
   * Call this function to set value for only once
   * @param context field context
   * @param pathArg storage path
   * @param value new value
   */
  public setValueIfEmpty = (context: LogicFnContext, pathArg: string, value: PrimitiveType): void => {
    const iterationId = getIterationId(context);
    const pageId = getPageIterationId(context);
    const state = this.statefulService.wrap(pathArg, iterationId, pageId);
    const storedValue = state.value;
    if (typeof storedValue === 'undefined' || storedValue === null || String(storedValue) === '') {
      state.value = value;
    }
  };
  /**
   * Trigger adding a new card (= new array item)
   * @param context LogicFnContext
   * @param pathArrayRoot e.g. 'employments' for 'api:employments/$/xyz'
   */
  public addNewCard = (context: LogicFnContext, pathArrayRoot: string): void => {
    this.statefulService.arrayAdd(processPath(pathArrayRoot, context.page.pageIterationId));
  };

  /**
   * Trigger adding a new card with callback logicFn function (= new array item)
   * @param _context LogicFnContext
   * @param pathArrayRoot e.g. 'employments' for 'api:employments/$/xyz'
   */
  public addNewCardWithCallback = (_context: LogicFnContext, pathArrayRoot: string, callBackLogicFn: LogicFnRef): void => {
    this.statefulService.arrayAdd(processPath(pathArrayRoot, _context.page.pageIterationId), () => {
      this.logicservice.evaluate([callBackLogicFn], _context);
    });
  };

  public confirmBeforeRemovecard = (context: LogicFnContext, confirmTxt: string, path: string): void => {
    this.statefulService.wrap('ss:beneficiaryRemovePath').value = path;
    this.statefulService.wrap('ss:beneficiaryPageIterationId').value = context.card.pageIterationId;
    this.statefulService.wrap('ss:beneficiaryRemoveCardId').value = context.card.config.id;
    this.statefulService.wrap('ss:beneficiaryRemoveCardIterationId').value = context.card.iterationId;
    this.statefulService.wrap('ss:showBeneficiaryRemoveModal').value = true;
  };

  public setFocusOAOCancel = (context: LogicFnContext): void => {
    if (context?.card?.id.includes('oao-beneficiary')) {
      const elementId = `remove-${context.card.id}`;
      if (elementId) {
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) {
            element.focus();
          }
        }, 100);
      }
    }
  };

  public setFocusOAO = (context: LogicFnContext): void => {
    if (context?.card?.id.includes('oao-beneficiary')) {
      const removeId = `remove-${context.card.id}`;
      const iterationId = context.card.iterationId;
      const removeBeneficiary = `remove-oao-beneficiary-${iterationId - 1}`;
      if (removeId.includes('remove-oao-beneficiary-0')) {
        document.getElementById('oao-add-beneficiary-0').focus();
      } else {
        document.getElementById(removeBeneficiary).focus();
      }
    }
  };

  public confirmBeforeClearArray = (context: LogicFnContext, confirmTxt: string, refValue: PrimitiveType, path: string): void => {
    const arraySize = this.statefulService.arraySize(path);
    if (context.field.data.value === refValue && arraySize > 0) {
      this.statefulService.wrap('ss:beneficiaryRemovePath').value = '';
      this.statefulService.wrap('ss:beneficiaryPageIterationId').value = '';
      this.statefulService.wrap('ss:beneficiaryRemoveCardId').value = '';
      this.statefulService.wrap('ss:beneficiaryRemoveCardIterationId').value = '';
      this.statefulService.wrap('ss:showBeneficiaryRemoveModal').value = true;
    }
  };

  public saveBundle = (context: LogicFnContext): void => {
    const urlArray = ['/user/login', '/consumer/dashboard', '/postapp/overview'];
    if (context.page && urlArray.filter((t) => context.routeEvent?.url?.includes(t))?.length === 0) {
      // Trigger a partial save
      this.applicationApiService.partialApplication = context.page.allowSavePartialApp && context.page.helper.isPageComplete;
      // Add a generateAppId URL params to the applicationApiService
      this.applicationApiService.generateAppId = context.page.generateAppId;

      // Using the page.id to clear out the Session Storage
      if (this.featureFlagService.isFeatureEnabled('SaveAppDataToSessionStorage')) {
        this.applicationApiService.updateBundleDataToDDMS(
          LoanType.OAO.toLowerCase(),
          null,
          null,
          context.page.id.toString() === 'oao-application-review'
        );
      } else {
        this.applicationApiService.updateBundleDataToDDMS(LoanType.OAO.toLowerCase());
      }
    }
  };
}
